import { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Plus, Search, Calendar, Clock, User, Phone, Mail,
  CheckCircle, AlertTriangle, TrendingUp, FileText, Euro,
  ChevronRight, Target, Award, Activity, MessageSquare,
  Play, Pause, Check, X, Edit2, Trash2, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Étapes du débourrage
const ETAPES_DEBOURRAGE = [
  { id: 'contact', nom: 'Prise de contact', duree_jours: 7, description: 'Habituation au toucher, voix, présence humaine' },
  { id: 'manipulation', nom: 'Manipulation', duree_jours: 14, description: 'Licol, attaches, brossage, pieds' },
  { id: 'travail_sol', nom: 'Travail au sol', duree_jours: 21, description: 'Longe, désensibilisation, transitions' },
  { id: 'sellerie', nom: 'Introduction sellerie', duree_jours: 14, description: 'Tapis, selle, sangle, enrênement' },
  { id: 'montoir', nom: 'Montoir', duree_jours: 7, description: 'Acceptation du cavalier, premiers pas montés' },
  { id: 'perfectionnement', nom: 'Perfectionnement', duree_jours: 30, description: 'Directions, allures, extérieur' }
];

const STATUTS_DEBOURRAGE = [
  { value: 'en_attente', label: 'En attente', color: 'bg-gray-500' },
  { value: 'en_cours', label: 'En cours', color: 'bg-blue-500' },
  { value: 'en_pause', label: 'En pause', color: 'bg-yellow-500' },
  { value: 'termine', label: 'Terminé', color: 'bg-green-500' },
  { value: 'abandon', label: 'Abandonné', color: 'bg-red-500' }
];

export default function DebourrageEquinPage() {
  const { activeSociete } = useWorkspace();
  const [activeTab, setActiveTab] = useState('planning');
  const [loading, setLoading] = useState(true);
  const [debourrages, setDebourrages] = useState([]);
  const [stats, setStats] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [newDebourrage, setNewDebourrage] = useState({
    cheval_nom: '',
    cheval_age: '',
    cheval_race: '',
    proprietaire_nom: '',
    proprietaire_telephone: '',
    proprietaire_email: '',
    date_debut: new Date().toISOString().split('T')[0],
    tarif_total: 1500,
    notes: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'X-Societe-Id': activeSociete?.id || ''
    };

    try {
      const res = await fetch(`${API_URL}/api/equin/debourrages`, { headers });
      if (res.ok) {
        const data = await res.json();
        setDebourrages(data.debourrages || []);
        setStats(data.stats || {});
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  }, [activeSociete]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateDebourrage = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/equin/debourrages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': activeSociete?.id || ''
        },
        body: JSON.stringify(newDebourrage)
      });

      if (res.ok) {
        toast.success('Débourrage créé avec succès');
        setShowAddModal(false);
        setNewDebourrage({
          cheval_nom: '', cheval_age: '', cheval_race: '',
          proprietaire_nom: '', proprietaire_telephone: '', proprietaire_email: '',
          date_debut: new Date().toISOString().split('T')[0], tarif_total: 1500, notes: ''
        });
        fetchData();
      } else {
        toast.error('Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleUpdateEtape = async (debourrageId, etapeId, statut) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/equin/debourrages/${debourrageId}/etapes/${etapeId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': activeSociete?.id || ''
        },
        body: JSON.stringify({ statut, date_validation: new Date().toISOString() })
      });

      if (res.ok) {
        toast.success('Étape mise à jour');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const filteredDebourrages = debourrages.filter(d =>
    d.cheval_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.proprietaire_nom?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (statut) => {
    const s = STATUTS_DEBOURRAGE.find(st => st.value === statut);
    return s ? <Badge className={`${s.color} text-white`}>{s.label}</Badge> : <Badge>{statut}</Badge>;
  };

  const getProgressPercent = (debourrage) => {
    if (!debourrage.etapes) return 0;
    const completed = debourrage.etapes.filter(e => e.statut === 'termine').length;
    return Math.round((completed / ETAPES_DEBOURRAGE.length) * 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 p-6" data-testid="debourrage-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-amber-900">Débourrage Équin</h1>
            <p className="text-amber-700">Suivi des programmes de débourrage et éducation des jeunes chevaux</p>
          </div>
          <Button onClick={() => setShowAddModal(true)} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="w-4 h-4 mr-2" /> Nouveau débourrage
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white/80 backdrop-blur">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">En cours</p>
                <p className="text-2xl font-bold text-amber-600">{stats.en_cours || 0}</p>
              </div>
              <Activity className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/80 backdrop-blur">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Terminés ce mois</p>
                <p className="text-2xl font-bold text-green-600">{stats.termines_mois || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/80 backdrop-blur">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">En attente</p>
                <p className="text-2xl font-bold text-gray-600">{stats.en_attente || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-gray-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/80 backdrop-blur">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">CA Mensuel</p>
                <p className="text-2xl font-bold text-blue-600">{stats.ca_mensuel || 0} €</p>
              </div>
              <Euro className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white/80">
          <TabsTrigger value="planning">Planning</TabsTrigger>
          <TabsTrigger value="chevaux">Chevaux en cours</TabsTrigger>
          <TabsTrigger value="historique">Historique</TabsTrigger>
          <TabsTrigger value="methode">Méthode</TabsTrigger>
        </TabsList>

        <TabsContent value="planning">
          <Card className="bg-white/80 backdrop-blur">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Planning des debourrages</CardTitle>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Chargement...</div>
              ) : filteredDebourrages.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Target className="w-12 h-12 mx-auto mb-4 text-amber-300" />
                  <p>Aucun débourrage en cours</p>
                  <Button onClick={() => setShowAddModal(true)} variant="outline" className="mt-4">
                    <Plus className="w-4 h-4 mr-2" /> Ajouter un premier débourrage
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredDebourrages.map((debourrage) => (
                    <div
                      key={debourrage.id}
                      className="border rounded-lg p-4 hover:bg-amber-50 transition cursor-pointer"
                      onClick={() => setShowDetailModal(debourrage)}
                      data-testid={`debourrage-${debourrage.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                            <span className="text-xl">🐴</span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{debourrage.cheval_nom}</h3>
                            <p className="text-sm text-gray-500">
                              {debourrage.cheval_race} • {debourrage.cheval_age} ans
                            </p>
                            <p className="text-sm text-gray-500">
                              <User className="w-3 h-3 inline mr-1" />
                              {debourrage.proprietaire_nom}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="mb-1">{getStatusBadge(debourrage.statut)}</div>
                            <p className="text-sm text-gray-500">
                              Début: {new Date(debourrage.date_debut).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <div className="w-24">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span>Progression</span>
                              <span>{getProgressPercent(debourrage)}%</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full">
                              <div
                                className="h-2 bg-amber-500 rounded-full transition-all"
                                style={{ width: `${getProgressPercent(debourrage)}%` }}
                              />
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="chevaux">
          <Card className="bg-white/80 backdrop-blur">
            <CardHeader>
              <CardTitle>Chevaux en débourrage</CardTitle>
              <CardDescription>Suivi détaillé des étapes pour chaque cheval</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {filteredDebourrages.filter(d => d.statut === 'en_cours').map((debourrage) => (
                  <div key={debourrage.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">🐴</span>
                        <div>
                          <h3 className="font-semibold">{debourrage.cheval_nom}</h3>
                          <p className="text-sm text-gray-500">{debourrage.proprietaire_nom}</p>
                        </div>
                      </div>
                      {getStatusBadge(debourrage.statut)}
                    </div>
                    <div className="grid grid-cols-6 gap-2">
                      {ETAPES_DEBOURRAGE.map((etape, idx) => {
                        const etapeData = debourrage.etapes?.find(e => e.etape_id === etape.id);
                        const isCompleted = etapeData?.statut === 'termine';
                        const isCurrent = etapeData?.statut === 'en_cours';
                        return (
                          <div
                            key={etape.id}
                            className={`p-2 rounded text-center text-xs ${
                              isCompleted ? 'bg-green-100 text-green-800' :
                              isCurrent ? 'bg-amber-100 text-amber-800 ring-2 ring-amber-400' :
                              'bg-gray-100 text-gray-600'
                            }`}
                          >
                            <div className="font-medium">{etape.nom}</div>
                            {isCompleted && <Check className="w-4 h-4 mx-auto mt-1 text-green-600" />}
                            {isCurrent && <Activity className="w-4 h-4 mx-auto mt-1 text-amber-600 animate-pulse" />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="historique">
          <Card className="bg-white/80 backdrop-blur">
            <CardHeader>
              <CardTitle>Historique des debourrages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {debourrages.filter(d => d.statut === 'termine').map((debourrage) => (
                  <div key={debourrage.id} className="flex items-center justify-between p-3 border rounded hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <div>
                        <span className="font-medium">{debourrage.cheval_nom}</span>
                        <span className="text-gray-500 ml-2">({debourrage.proprietaire_nom})</span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {debourrage.date_fin ? new Date(debourrage.date_fin).toLocaleDateString('fr-FR') : '-'}
                    </div>
                  </div>
                ))}
                {debourrages.filter(d => d.statut === 'termine').length === 0 && (
                  <p className="text-center text-gray-500 py-4">Aucun débourrage terminé</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="methode">
          <Card className="bg-white/80 backdrop-blur">
            <CardHeader>
              <CardTitle>Méthode de débourrage</CardTitle>
              <CardDescription>Programme en 6 étapes progressives</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {ETAPES_DEBOURRAGE.map((etape, idx) => (
                  <div key={etape.id} className="flex items-start gap-4 p-4 border rounded-lg">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center font-bold text-amber-700">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{etape.nom}</h3>
                      <p className="text-gray-600">{etape.description}</p>
                      <Badge variant="outline" className="mt-2">
                        <Clock className="w-3 h-3 mr-1" /> {etape.duree_jours} jours
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Ajout */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouveau débourrage</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom du cheval *</Label>
                <Input
                  value={newDebourrage.cheval_nom}
                  onChange={(e) => setNewDebourrage({ ...newDebourrage, cheval_nom: e.target.value })}
                  placeholder="Ex: Eclipse"
                />
              </div>
              <div>
                <Label>Âge (années)</Label>
                <Input
                  type="number"
                  value={newDebourrage.cheval_age}
                  onChange={(e) => setNewDebourrage({ ...newDebourrage, cheval_age: e.target.value })}
                  placeholder="3"
                />
              </div>
            </div>
            <div>
              <Label>Race</Label>
              <Input
                value={newDebourrage.cheval_race}
                onChange={(e) => setNewDebourrage({ ...newDebourrage, cheval_race: e.target.value })}
                placeholder="Selle Français"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Propriétaire *</Label>
                <Input
                  value={newDebourrage.proprietaire_nom}
                  onChange={(e) => setNewDebourrage({ ...newDebourrage, proprietaire_nom: e.target.value })}
                  placeholder="Nom complet"
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={newDebourrage.proprietaire_telephone}
                  onChange={(e) => setNewDebourrage({ ...newDebourrage, proprietaire_telephone: e.target.value })}
                  placeholder="06..."
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={newDebourrage.date_debut}
                  onChange={(e) => setNewDebourrage({ ...newDebourrage, date_debut: e.target.value })}
                />
              </div>
              <div>
                <Label>Tarif total (€)</Label>
                <Input
                  type="number"
                  value={newDebourrage.tarif_total}
                  onChange={(e) => setNewDebourrage({ ...newDebourrage, tarif_total: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newDebourrage.notes}
                onChange={(e) => setNewDebourrage({ ...newDebourrage, notes: e.target.value })}
                placeholder="Observations, particularités..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Annuler</Button>
            <Button onClick={handleCreateDebourrage} className="bg-amber-600 hover:bg-amber-700">
              Créer le débourrage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Détail */}
      <Dialog open={!!showDetailModal} onOpenChange={() => setShowDetailModal(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              <span className="text-2xl mr-2">🐴</span>
              {showDetailModal?.cheval_nom}
            </DialogTitle>
          </DialogHeader>
          {showDetailModal && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Race</p>
                  <p className="font-medium">{showDetailModal.cheval_race || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Âge</p>
                  <p className="font-medium">{showDetailModal.cheval_age} ans</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Propriétaire</p>
                  <p className="font-medium">{showDetailModal.proprietaire_nom}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Téléphone</p>
                  <p className="font-medium">{showDetailModal.proprietaire_telephone || '-'}</p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Progression des étapes</h4>
                <div className="space-y-2">
                  {ETAPES_DEBOURRAGE.map((etape) => {
                    const etapeData = showDetailModal.etapes?.find(e => e.etape_id === etape.id);
                    return (
                      <div key={etape.id} className="flex items-center justify-between p-3 border rounded">
                        <div>
                          <span className="font-medium">{etape.nom}</span>
                          <span className="text-sm text-gray-500 ml-2">({etape.duree_jours}j)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {etapeData?.statut === 'termine' ? (
                            <Badge className="bg-green-500">Terminé</Badge>
                          ) : etapeData?.statut === 'en_cours' ? (
                            <Badge className="bg-amber-500">En cours</Badge>
                          ) : (
                            <Badge variant="outline">À faire</Badge>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUpdateEtape(showDetailModal.id, etape.id, 'termine')}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {showDetailModal.notes && (
                <div>
                  <h4 className="font-semibold mb-2">Notes</h4>
                  <p className="text-gray-600 bg-gray-50 p-3 rounded">{showDetailModal.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
